import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { parseFen } from '@libs/chess/parseFen';
import { IPgnVariantMeta, IPgnMove, PgnVariant, Pgn } from '@libs/chess/pgn';

import { INavMenuItem, IMoveMeta, Props } from '../PgnEditor';
import {
  addMoves,
  deleteMove,
  findVariantById,
  findMoveById,
  findMovePosition,
  moveVariant,
  promoteVariant,
  updateVariant,
  updateMove,
} from '../services/moves';
import { getNavigationMoves } from '../services/navigation';
import { IGameCommentTarget, IVariantCommentTarget } from '../services/viewModel';

/*
    ____,-------------------------------,____
    \   |             Хук               |   /
    /___|-------------------------------|___\
*/

export function usePgnEditor(
  externalPgn: Pgn<IPgnMove> | undefined,
  {
    readonlyMainVariant = false,
    initialSelected,
  }: {
    readonlyMainVariant?: boolean;
    initialSelected?: IPgnMove | null;
  } = {},
) {
  const [pgn, setPgn] = useState<Pgn<IPgnMove>>(() => externalPgn ?? new Pgn<IPgnMove>());

  // Цвет и номер первого хода — из стартового FEN живого pgn: "Расставить
  // фигуры" меняет позицию без ремаунта, а undo/redo возвращают ее обратно
  const startFen = useMemo(() => parseFen(pgn.fen), [pgn.fen]);
  const startColor = startFen?.color ?? 'white';
  const startFullMove = startFen?.fullMoves ?? 1;

  const [meta, setMeta] = useState<Record<string, IMoveMeta>>({});

  const [selected, setSelected] = useState<IPgnMove | undefined>(initialSelected ?? undefined);
  const [showMenu, setShowMenu] = useState<IPgnMove | undefined>();

  const _store = {
    pgn,
    selected,
    readonlyMainVariant,
    saveUndoSnapshot: (pgn: Pgn<IPgnMove>) => {
      const h = historyRef.current;
      const p = historyPosRef.current;

      if (p === null) {
        h.push({ pgn });
      } else {
        const last = h[p];

        if (last?.pgn !== pgn) {
          historyRef.current = h.slice(0, p + 1).concat({ pgn });
          historyPosRef.current = null;
        }
      }
    },
  };
  const ref = useRef(_store);
  ref.current = _store;

  const { first, last, next, prev, alts, playSequence } = useMemo(
    () => getNavigationMoves(selected?.id, pgn.moves),
    [selected, pgn.moves],
  );

  // ----- Подсветка опций NavMenu

  const [highlightedId, setHighlightedId] = useState<string | undefined>();

  useEffect(() => {
    setHighlightedId(undefined);
  }, [selected, pgn.moves]);

  const navItems = useMemo<INavMenuItem[] | undefined>(() => {
    const list: INavMenuItem[] = [];

    let switchAnchor: IPgnMove | undefined;
    let skipVariantId: string | undefined;
    if (selected) {
      if (selected.altId) {
        const variant = findVariantById(selected.altId, pgn.moves);
        if (variant) {
          const firstNonStub = variant.moves.find((m) => m.san !== '--');
          if (firstNonStub === selected) {
            switchAnchor = findMoveById(variant.parentMoveId, pgn.moves);
            skipVariantId = selected.altId;
          } else {
            switchAnchor = selected;
          }
        }
      } else {
        switchAnchor = selected;
      }
    }
    if (switchAnchor) {
      if (switchAnchor !== selected) {
        list.push({ type: 'switch', move: switchAnchor });
      }
      switchAnchor.alts?.forEach((v) => {
        if (v.id === skipVariantId) return;
        const m = v.moves.find((mm) => mm.san !== '--');
        if (m) list.push({ type: 'switch', move: m });
      });
    }

    const hasSwitches = list.length > 0;
    const hasContinueAlts = !!alts?.length;
    if (next && (hasContinueAlts || hasSwitches)) {
      list.push({ type: 'continue', move: next });
    }
    alts?.forEach((m) => list.push({ type: 'continue', move: m }));

    return list.length ? list : undefined;
  }, [alts, next, selected, pgn.moves]);

  const navItemsRef = useRef(navItems);
  navItemsRef.current = navItems;

  const onNavMenuCycle = useCallback((direction: 'up' | 'down') => {
    setHighlightedId((currentId) => {
      const list = navItemsRef.current;
      if (!list?.length) return currentId;
      const idx = currentId ? list.findIndex((e) => e.move.id === currentId) : -1;
      const len = list.length;
      const nextIdx =
        idx === -1
          ? direction === 'down'
            ? 0
            : len - 1
          : direction === 'down'
            ? (idx + 1) % len
            : (idx - 1 + len) % len;
      return list[nextIdx]!.move.id;
    });
  }, []);

  const onSelectedBlur = useCallback(() => {
    setHighlightedId(undefined);
  }, []);

  // ----

  // эффективный next: подсветка переопределяет дефолт только для 'continue'
  // опций (вперёд по дереву). Для 'switch' (сиблингов) ArrowRight оставляем
  // как дефолтную навигацию вперёд.
  const highlightedEntry = highlightedId
    ? navItems?.find((e) => e.move.id === highlightedId)
    : undefined;
  const effectiveNextId = highlightedEntry?.type === 'continue' ? highlightedId : next?.id;

  const lastIdRef = useRef(0);
  const createId = () => `editor-${lastIdRef.current++}`;

  // ----- Новые ходы их API

  // TODO: избавиться от двойного рендера из-за setPgn()
  useEffect(() => {
    setPgn((p) => {
      const sameMoves =
        p.moves === externalPgn?.moves || (!externalPgn?.moves.length && !p.moves.length);

      const samePrelude =
        p.prelude === externalPgn?.prelude &&
        p.coloredArrows === externalPgn?.coloredArrows &&
        p.coloredSquares === externalPgn?.coloredSquares;

      if (sameMoves && samePrelude) {
        return p;
      }

      const selected = ref.current.selected;
      const isLastSelected = selected === p.moves[p.moves.length - 1];

      const nextMoves = externalPgn?.moves ? mergeMoves(p.moves, externalPgn.moves) : [];
      const nextSelected = selected
        ? isLastSelected
          ? nextMoves[nextMoves.length - 1]
          : findMoveById(selected.id, nextMoves)
        : undefined;

      setSelected(nextSelected);
      setShowMenu((s) => (s ? nextSelected : undefined));

      if (readonlyMainVariant) {
        // sic! меняем in-place т.к. объекты истории никогда
        // напрямую не сравниваются/не используются в реактивных flow
        historyRef.current.forEach((h) => {
          h.pgn = h.pgn.copy({
            moves: mergeMoves(h.pgn.moves, nextMoves),
          });
        });
      } else {
        historyRef.current = [];
        historyPosRef.current = null;
      }

      return externalPgn ? externalPgn.copy({ moves: nextMoves }) : p.copy({ moves: nextMoves });
    });
  }, [externalPgn, readonlyMainVariant]);

  // ----- History (undo + redo)

  const historyRef = useRef<{ pgn: Pgn<IPgnMove>; selected?: IPgnMove }[]>([{ pgn, selected }]);
  const historyPosRef = useRef<number | null>(null);
  const canUndo = Boolean(
    historyRef.current.length > 1 && (historyPosRef.current === null || historyPosRef.current > 0),
  );
  const canRedo = Boolean(
    historyPosRef.current !== null && historyPosRef.current < historyRef.current.length - 1,
  );
  useEffect(() => {
    const h = historyRef.current;
    const p = historyPosRef.current;
    const cur = p === null ? h[h.length - 1] : h[p];

    if (cur && pgn.moves === cur?.pgn.moves) {
      cur.selected = selected;
    }
  }, [pgn.moves, selected]);

  // ----- Действия

  const addManyMoves = useCallback((newMoves: IPgnMove[], after?: IPgnMove) => {
    const pgn = ref.current.pgn;
    const { nextMoves, nextSelected } = addMoves(after, newMoves, pgn.moves, createId, {
      readonlyMainVariant: ref.current.readonlyMainVariant,
    });
    if (nextMoves === pgn.moves && nextSelected === undefined) return;

    const nextPgn = pgn.copy({ moves: nextMoves });
    setPgn(nextPgn);
    setSelected(nextSelected);
    ref.current.saveUndoSnapshot(nextPgn);
    setShowMenu(undefined);
  }, []);

  const addManySans = useCallback(
    (sans: string[], after?: IPgnMove) => {
      if (!sans.length) return;

      // найдём color и moveFull для первого добавляемого хода
      let color: 'white' | 'black';
      let moveFull: number;

      const firstMove = ref.current.pgn.moves[0];

      if (after) {
        color = after.color === 'white' ? 'black' : 'white';
        moveFull = after.color === 'black' ? after.moveFull + 1 : after.moveFull;
      } else if (firstMove) {
        color = firstMove.color;
        moveFull = firstMove.moveFull;
      } else {
        color = startColor;
        moveFull = startFullMove;
      }

      const newMoves: IPgnMove[] = sans.map((san) => {
        const move: IPgnMove = { id: createId(), color, moveFull, san };
        const nextColor = color === 'white' ? 'black' : 'white';
        const nextMoveFull = color === 'black' ? moveFull + 1 : moveFull;
        color = nextColor;
        moveFull = nextMoveFull;
        return move;
      });

      addManyMoves(newMoves, after);
    },
    [startColor, startFullMove, addManyMoves],
  );

  const addSan = useCallback(
    (san: string, after?: IPgnMove) => addManySans([san], after),
    [addManySans],
  );

  const updateMoveScore = useCallback((id: string, type: 'cp' | 'mate', value: number) => {
    // обновляем мета-данные только для основной ветки
    const isMain = ref.current.pgn.moves.find((m) => id === m.id);
    if (!isMain) return;

    setMeta((m) => {
      const prev = m[id];

      // ничего не изменилось
      if (type === prev?.score?.type && value === prev?.score.value) return m;

      const data: IMoveMeta = { ...prev, score: { type, value } };
      return { ...m, [id]: data };
    });
  }, []);

  const updateVariantMeta = useCallback(
    (alt: PgnVariant | undefined, data: IPgnVariantMeta, skipSnapshot?: boolean) => {
      const pgn = ref.current.pgn;
      const moves = pgn.moves;

      if (alt) {
        // обновляем вариант
        const { nextMoves } = updateVariant(alt, moves, {
          action: 'update',
          data,
        });
        const nextPgn = pgn.copy({ moves: nextMoves });
        setPgn(nextPgn);
        if (!skipSnapshot) {
          ref.current.saveUndoSnapshot(nextPgn);
        }
      } else {
        // обновляем "вступление" всего pgn
        const nextPgn = pgn.copy({
          ...data,
          moves, // на случай, если в data на самом деле пробросили pgn
        });
        setPgn(nextPgn);
        if (!skipSnapshot) {
          ref.current.saveUndoSnapshot(nextPgn);
        }
      }
    },
    [],
  );

  const updateMoveData = useCallback((m: IPgnMove, data: Partial<IPgnMove>) => {
    const pgn = ref.current.pgn;

    const { nextMoves, nextSelected } = updateMove(m, data, pgn.moves);
    const nextPgn = pgn.copy({ moves: nextMoves });
    setPgn(nextPgn);
    if (ref.current.selected === m) setSelected(nextSelected);
    ref.current.saveUndoSnapshot(nextPgn);
  }, []);

  // Обновить pgn целиком тем же контрактом, что у Pgn.copy: rawTags заменяет
  // весь набор тэгов, остальное сохраняется, если не передано явно. Один
  // снимок истории на правку — откатывается наравне с ходами
  const updatePgn = useCallback((updates: NonNullable<Parameters<Pgn<IPgnMove>['copy']>[0]>) => {
    const pgn = ref.current.pgn;
    const nextPgn = pgn.copy(updates);
    setPgn(nextPgn);
    ref.current.saveUndoSnapshot(nextPgn);
    // Ходы заменили — старый выбор указывает в никуда
    if (updates.moves) setSelected(undefined);
  }, []);

  // ----- Callbacks

  const onUndo = useCallback(() => {
    const h = historyRef.current;
    const p = historyPosRef.current ?? h.length - 1;

    const prevP = p - 1;
    const prev = h[prevP];
    if (prev) {
      historyPosRef.current = prevP;
      setPgn(prev.pgn);
      setSelected(prev.selected);
      setShowMenu(undefined);
    }
  }, []);

  const onRedo = useCallback(() => {
    const h = historyRef.current;
    const p = historyPosRef.current;

    if (p === null) return;

    const nextP = p + 1;
    const nextState = h[nextP];
    if (nextState) {
      historyPosRef.current = nextP;
      setPgn(nextState.pgn);
      setSelected(nextState.selected);
      setShowMenu(undefined);
    }
  }, []);

  const onMoveSelect = useCallback((id: string | undefined) => {
    if (id) {
      const pos = findMovePosition(id, ref.current.pgn.moves);
      if (pos) setSelected(pos.cur);
    } else {
      setSelected(undefined);
    }
    setShowMenu(undefined);
  }, []);

  const onMenuOpen = useCallback((id: string) => {
    const pos = findMovePosition(id, ref.current.pgn.moves);
    if (pos) {
      setSelected(pos.cur);
      setShowMenu(pos.cur);
      setHighlightedId(undefined);
    }
  }, []);

  const onMenuClose = useCallback(() => {
    setShowMenu(undefined);
  }, []);

  const onVariantMove = useCallback((m: IPgnMove, direction: 'up' | 'down') => {
    if (!m.altId) return;
    const pgn = ref.current.pgn;
    const moves = pgn.moves;
    const variant = findVariantById(m.altId, moves);
    if (!variant) return;

    const { nextMoves } = moveVariant(variant, direction, moves);
    if (nextMoves === moves) return;

    const nextPgn = pgn.copy({ moves: nextMoves });
    setPgn(nextPgn);
    ref.current.saveUndoSnapshot(nextPgn);
    setShowMenu(undefined);
  }, []);

  const onVariantPromote = useCallback((m: IPgnMove) => {
    if (!m.altId) return;

    const pgn = ref.current.pgn;
    const moves = pgn.moves;
    const variant = findVariantById(m.altId, moves);

    if (!variant) return;

    const { nextMoves, nextSelected } = promoteVariant(variant, 'swap', moves, createId);
    const nextPgn = pgn.copy({ moves: nextMoves });
    setPgn(nextPgn);
    setSelected(nextSelected);
    ref.current.saveUndoSnapshot(nextPgn);
    setShowMenu(undefined);
  }, []);

  const onMoveDelete = useCallback((m: IPgnMove) => {
    const pgn = ref.current.pgn;
    const moves = pgn.moves;

    // если у удаляемого хода 2+ подветок — промоутим первую в режиме 'replace'
    // вместо удаления хода, т.к. такой ход отображается как "виртуальная" ветка в дереве вариантов
    // и интуитивней будет не сносить всё дерево, а удалить только "виртуальную" ветку, оставив остальные
    const altToPromote = m.alts && m.alts.length > 1 ? m.alts[0] : undefined;
    const { nextMoves, nextSelected } = altToPromote
      ? promoteVariant(altToPromote, 'replace', moves, createId)
      : deleteMove(m, moves);

    const nextPgn = pgn.copy({ moves: nextMoves });
    setPgn(nextPgn);
    setSelected(nextSelected);
    ref.current.saveUndoSnapshot(nextPgn);
    setShowMenu(undefined);
  }, []);

  const onMoveCommentCreate = useCallback((m: IPgnMove) => {
    const pgn = ref.current.pgn;

    setShowMenu(undefined);

    const { nextMoves, nextSelected } = updateMove(m, { comment: m.comment ?? '' }, pgn.moves);
    const nextPgn = pgn.copy({ moves: nextMoves });
    setPgn(nextPgn);
    setSelected(nextSelected);
    // sic! пока коммент пустой — не создаём снэпшот
    // ref.current.saveUndoSnapshot(nextMoves, nextSelected);
  }, []);

  const onMoveCommentUpdate = useCallback((comment: string, m: IPgnMove) => {
    const pgn = ref.current.pgn;

    const normalized = normalizeComment(comment);
    if (m.comment === normalized) return;

    const { nextMoves, nextSelected } = updateMove(m, { comment: normalized }, pgn.moves);
    const nextPgn = pgn.copy({ moves: nextMoves });
    setPgn(nextPgn);
    if (ref.current.selected === m) setSelected(nextSelected);
    ref.current.saveUndoSnapshot(nextPgn);
  }, []);

  const onPreludeCreate = useCallback(
    (m: IGameCommentTarget | IVariantCommentTarget) => {
      setShowMenu(undefined);

      // основная ветка
      if (m.type === 'main') {
        // sic! пока коммент пустой — не создаём снэпшот
        updateVariantMeta(undefined, { prelude: '' }, true);
        return;
      }

      // альтернативная ветка
      const variant = findVariantById(m.variantId, ref.current.pgn.moves);
      if (!variant) return;
      // sic! пока коммент пустой — не создаём снэпшот
      updateVariantMeta(variant, { prelude: '' }, true);
    },
    [updateVariantMeta],
  );

  const onPreludeUpdate = useCallback(
    (comment: string, m: IGameCommentTarget | IVariantCommentTarget) => {
      const normalized = normalizeComment(comment);
      if (m.comment === normalized) return;

      // основная ветка
      if (m.type === 'main') {
        updateVariantMeta(undefined, { prelude: normalized });
        return;
      }

      // альтернативная ветка
      const variant = findVariantById(m.variantId, ref.current.pgn.moves);
      if (variant) {
        updateVariantMeta(variant, { prelude: normalized });
      }
    },
    [updateVariantMeta],
  );

  const onNagsUpdate = useCallback((nags: number[] | undefined, m: IPgnMove) => {
    const pgn = ref.current.pgn;

    const { nextMoves, nextSelected } = updateMove(m, { nags }, pgn.moves);
    const nextPgn = pgn.copy({ moves: nextMoves });
    setPgn(nextPgn);
    setSelected(nextSelected);
    ref.current.saveUndoSnapshot(nextPgn);
    setShowMenu(undefined);
  }, []);

  const onPointsUpdate = useCallback((points: number | undefined, m: IPgnMove) => {
    const pgn = ref.current.pgn;

    const { nextMoves, nextSelected } = updateMove(m, { points }, pgn.moves);
    const nextPgn = pgn.copy({ moves: nextMoves });
    setPgn(nextPgn);
    setSelected(nextSelected);
    ref.current.saveUndoSnapshot(nextPgn);
    setShowMenu(undefined);
  }, []);

  // ----- Props

  const props: Props = {
    pgn,
    meta,
    //
    selected: selected?.id,
    showMenu: Boolean(showMenu?.id && showMenu.id === selected?.id),
    //
    first: first === null ? null : first?.id,
    last: last?.id,
    next: effectiveNextId,
    prev: prev === null ? null : prev?.id,
    highlighted: highlightedId,
    navItems,
    //
    canUndo,
    canRedo,
    //
    startColor,
    startFullMove,
    //
    readonlyMainVariant,
    //
    onMoveSelect,
    onSelectedBlur,
    onMenuOpen,
    onMenuClose,
    onMoveDelete,
    onVariantPromote,
    onVariantMove,
    onMoveCommentCreate,
    onMoveCommentUpdate,
    onPreludeCreate,
    onPreludeUpdate,
    onNagsUpdate,
    onPointsUpdate,
    onUndo,
    onRedo,
    onNavMenuCycle,
  };

  // ----- Ahh, profit

  return {
    pgn,
    selected,
    first,
    last,
    next,
    prev,
    playSequence,
    addSan,
    addManySans,
    addManyMoves,
    updateVariantMeta,
    updateMoveScore,
    updateMoveData,
    updatePgn,
    props,
  };
}

/*
    ____,-------------------------------,____
    \   |            Утилиты            |   /
    /___|-------------------------------|___\
*/

function normalizeComment(comment?: string) {
  if (!comment || !comment.trim()) return undefined;
  return comment
    .replaceAll(/[\r}]/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function mergeMoves(movesPrev: IPgnMove[], movesNext: IPgnMove[]): IPgnMove[] {
  const moves: IPgnMove[] = [];
  let diverged = false;
  for (let i = 0; i < movesNext.length; i++) {
    if (movesNext.length < movesPrev.length) {
      return movesNext;
    }

    const prev = movesPrev[i];
    const next = movesNext[i]!;

    if (!diverged && prev && prev.san === next.san) {
      moves.push(prev);
    } else {
      moves.push(next);
      diverged = true;
    }
  }

  return moves;
}
